import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Save, Image as ImageIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper for authenticated fetch
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('session_token');
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, credentials: 'include', headers });
};

// Convert Google Drive URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return url;
  if (url.includes('drive.google.com/thumbnail') || url.includes('lh3.googleusercontent.com')) return url;
  
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w1000`;
  
  const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w1000`;
  
  const ucMatch = url.match(/drive\.google\.com\/uc\?(?:export=view&)?id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w1000`;
  
  return url;
};

export const CompanyEditorPage = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const isNew = companyId === 'new';
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    type: '',
    tagline: '',
    description: '',
    logo: '',
    image: '',
    video_url: '',
    website: '',
    founded: '',
    location: ''
  });

  // Check auth
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/auth/me`);
        if (!response.ok) {
          navigate('/cms/login');
          return;
        }
        const userData = await response.json();
        if (userData.role !== 'admin') {
          navigate('/cms');
          return;
        }
        setUser(userData);
      } catch (error) {
        navigate('/cms/login');
      }
    };
    checkAuth();
  }, [navigate]);

  // Fetch company data (only if editing existing)
  useEffect(() => {
    if (!user) return;
    
    if (isNew) {
      setLoading(false);
      return;
    }

    const fetchCompany = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/companies/id/${companyId}`);
        if (!response.ok) {
          navigate('/cms');
          return;
        }
        const data = await response.json();
        setFormData({
          name: data.name || '',
          slug: data.slug || '',
          type: data.type || '',
          tagline: data.tagline || '',
          description: data.description || '',
          logo: data.logo || '',
          image: data.image || '',
          video_url: data.video_url || '',
          website: data.website || '',
          founded: data.founded || '',
          location: data.location || ''
        });
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, [companyId, navigate, user, isNew]);

  // Auto-generate slug from name
  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 50);
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      slug: formData.slug || generateSlug(name)
    });
  };

  const handleSave = async () => {
    if (!formData.name) {
      alert('Company name is required');
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        logo: convertGoogleDriveUrl(formData.logo),
        image: convertGoogleDriveUrl(formData.image),
        slug: formData.slug || generateSlug(formData.name)
      };

      const url = isNew 
        ? `${API_URL}/api/companies`
        : `${API_URL}/api/companies/${companyId}`;
      
      const method = isNew ? 'POST' : 'PUT';

      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSave)
      });

      if (response.ok) {
        navigate('/cms');
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.detail || 'Failed to save');
      }
    } catch (error) {
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="editorial-container py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/cms" className="text-xl editorial-serif">Cultural Revolution</Link>
            <span className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-1 bg-muted">
              {isNew ? 'New Company' : 'Edit Company'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/cms">
              <Button variant="ghost" size="sm">
                <ArrowLeft size={16} className="mr-2" />
                Back
              </Button>
            </Link>
            <Button onClick={handleSave} disabled={saving}>
              <Save size={16} className="mr-2" />
              {saving ? 'Saving...' : (isNew ? 'Create Company' : 'Save')}
            </Button>
          </div>
        </div>
      </header>

      <div className="editorial-container py-8">
        <div className="max-w-3xl mx-auto space-y-8">
          <Card className="p-6">
            <h2 className="text-xl editorial-serif mb-4">Company Details</h2>
            <div className="space-y-4">
              {/* Logo URL */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Logo URL
                  <span className="text-xs text-muted-foreground ml-2">(Use Google Drive share link)</span>
                </label>
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 bg-muted overflow-hidden flex-shrink-0 flex items-center justify-center">
                    {formData.logo ? (
                      <img src={convertGoogleDriveUrl(formData.logo)} alt="Logo" className="max-w-full max-h-full object-contain" />
                    ) : (
                      <ImageIcon size={24} className="text-muted-foreground" />
                    )}
                  </div>
                  <input
                    type="text"
                    value={formData.logo}
                    onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                    className="flex-1 px-3 py-2 bg-background border border-border rounded text-sm"
                    placeholder="https://drive.google.com/file/d/..."
                  />
                </div>
              </div>

              {/* Cover Image URL */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Cover Image URL
                  <span className="text-xs text-muted-foreground ml-2">(Use Google Drive share link)</span>
                </label>
                <div className="flex items-start gap-4">
                  <div className="w-40 h-24 bg-muted overflow-hidden flex-shrink-0">
                    {formData.image ? (
                      <img src={convertGoogleDriveUrl(formData.image)} alt="Cover" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon size={32} className="text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    className="flex-1 px-3 py-2 bg-background border border-border rounded text-sm"
                    placeholder="https://drive.google.com/file/d/..."
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={handleNameChange}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    placeholder="company-slug"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                  >
                    <option value="">Select type</option>
                    <option value="Production">Production Company</option>
                    <option value="Distribution">Distribution</option>
                    <option value="Studio">Studio</option>
                    <option value="Agency">Agency</option>
                    <option value="Publisher">Publisher</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tagline</label>
                  <input
                    type="text"
                    value={formData.tagline}
                    onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    placeholder="Short tagline"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  rows={4}
                  placeholder="Company description"
                />
              </div>

              {/* Video URL */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Video URL
                  <span className="text-xs text-muted-foreground ml-2">(Google Drive, YouTube, or Vimeo)</span>
                </label>
                <input
                  type="text"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded text-sm"
                  placeholder="https://drive.google.com/file/d/... or https://youtube.com/watch?v=..."
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Founded Year</label>
                  <input
                    type="text"
                    value={formData.founded}
                    onChange={(e) => setFormData({ ...formData, founded: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    placeholder="e.g., 2020"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  placeholder="e.g., Los Angeles, CA"
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CompanyEditorPage;
